import { Badge, Button, Card, LoadingContainer } from '@/components/ui';
import {
  sysadminService,
  type AsesoriaAuditadaDTO,
  type AuditoriaNombresAudiosResponse,
  type EmpresaAuditadaDTO,
  type ProblemaAuditoriaDTO,
} from '@/services/sysadmin.service';
import {
  AlertTriangle,
  Building2,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  FileWarning,
  FolderOpen,
  Music,
  RefreshCw,
  Search,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

function formatDuracion(seg: number): string {
  const h = Math.floor(seg / 3600);
  const m = Math.floor((seg % 3600) / 60);
  const s = seg % 60;
  const partes = [h ? `${h}h` : '', m ? `${m}m` : '', s ? `${s}s` : ''].filter(Boolean);
  return partes.length ? partes.join(' ') : '0s';
}

function empresaTieneProblemas(e: EmpresaAuditadaDTO): boolean {
  return e.problemas.length > 0 || e.audiosConProblemas > 0 || e.huerfanos.length > 0;
}

function ProblemaChip({ problema }: { problema: ProblemaAuditoriaDTO }) {
  const esError = problema.severidad === 'error';
  return (
    <span
      title={problema.mensaje}
      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold border ${
        esError ? 'bg-red-50 text-red-700 border-red-200' : 'bg-amber-50 text-amber-700 border-amber-200'
      }`}
    >
      {problema.codigo.replace(/_/g, ' ')}
    </span>
  );
}

function AsesoriaTabla({ asesoria, soloConProblemas }: { asesoria: AsesoriaAuditadaDTO; soloConProblemas: boolean }) {
  const audios = soloConProblemas ? asesoria.audios.filter((a) => a.problemas.length > 0) : asesoria.audios;
  if (audios.length === 0) return null;

  return (
    <div className="border-t border-gray-100">
      <div className="px-4 py-2 bg-gray-50/60 flex items-center gap-2 text-xs">
        <span className="font-semibold text-gray-800">
          {asesoria.orden === 1 ? 'Diagnóstico' : `Asesoría ${asesoria.orden != null ? asesoria.orden - 1 : `#${asesoria.asesoriaId}`}`}
        </span>
        <span className="font-mono text-gray-400">#{asesoria.asesoriaId}</span>
        <span className="text-gray-400">· {new Date(asesoria.fechaInicio).toISOString().slice(0, 10)}</span>
        <Badge variant="gray" className="text-[10px]">
          {asesoria.estado}
        </Badge>
        {asesoria.totalProblemas > 0 && (
          <Badge variant="red" className="text-[10px]">
            {asesoria.totalProblemas} con problemas
          </Badge>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs text-left">
          <thead className="text-gray-500 font-semibold border-b border-gray-100">
            <tr>
              <th className="px-4 py-1.5 w-20">Audio</th>
              <th className="px-3 py-1.5">Nombre en Drive</th>
              <th className="px-3 py-1.5 w-24">Grabación</th>
              <th className="px-3 py-1.5 w-24">Duración</th>
              <th className="px-3 py-1.5">Problemas</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {audios.map((a) => (
              <tr key={a.audioId} className={a.problemas.length ? 'bg-red-50/20' : ''}>
                <td className="px-4 py-2 font-bold text-gray-900">#{a.audioId}</td>
                <td className="px-3 py-2">
                  {a.nombre ? (
                    a.url ? (
                      <a
                        href={a.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-mono text-blue-600 hover:underline inline-flex items-center gap-1 break-all"
                      >
                        {a.nombre}
                        <ExternalLink className="w-3 h-3 shrink-0" />
                      </a>
                    ) : (
                      <span className="font-mono">{a.nombre}</span>
                    )
                  ) : (
                    <span className="text-gray-400 italic">Sin nombre</span>
                  )}
                  {a.nombreSugerido && (
                    <div className="text-[10px] text-gray-500 mt-0.5">
                      Sugerido: <span className="font-mono text-emerald-700">{a.nombreSugerido}</span>
                    </div>
                  )}
                </td>
                <td className="px-3 py-2 text-gray-500">{new Date(a.fechaGrabacion).toISOString().slice(0, 10)}</td>
                <td className="px-3 py-2 font-mono text-gray-700">{formatDuracion(a.duracionSeg)}</td>
                <td className="px-3 py-2">
                  {a.problemas.length === 0 ? (
                    <Badge variant="green" className="text-[10px]">
                      OK
                    </Badge>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {a.problemas.map((p) => (
                        <ProblemaChip key={p.codigo} problema={p} />
                      ))}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EmpresaCard({ empresa, soloConProblemas }: { empresa: EmpresaAuditadaDTO; soloConProblemas: boolean }) {
  const [abierta, setAbierta] = useState(false);
  const conProblemas = empresaTieneProblemas(empresa);

  return (
    <Card className="overflow-hidden border-gray-200">
      <button
        type="button"
        onClick={() => setAbierta((v) => !v)}
        className="w-full bg-gray-50 px-4 py-3 flex flex-col md:flex-row md:items-center justify-between gap-2 text-left cursor-pointer hover:bg-gray-100/70"
      >
        <div className="flex items-center gap-2 min-w-0">
          {abierta ? (
            <ChevronDown className="w-4 h-4 text-gray-500 shrink-0" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-500 shrink-0" />
          )}
          <span className="font-bold text-sm text-gray-900 truncate">{empresa.nombre}</span>
          <span className="text-[11px] font-mono text-gray-500">NIT: {empresa.nit}</span>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge variant="gray" className="text-[11px]">
            {empresa.totalAudios} audios
          </Badge>
          {empresa.audiosConProblemas > 0 && (
            <Badge variant="red" className="text-[11px]">
              {empresa.audiosConProblemas} con problemas
            </Badge>
          )}
          {empresa.huerfanos.length > 0 && (
            <Badge variant="warning" className="text-[11px]">
              {empresa.huerfanos.length} huérfanos
            </Badge>
          )}
          {empresa.problemas.map((p) => (
            <ProblemaChip key={p.codigo} problema={p} />
          ))}
          {!conProblemas && (
            <Badge variant="green" className="text-[11px]">
              Todo en orden
            </Badge>
          )}
        </div>
      </button>

      {abierta && (
        <div>
          {(empresa.problemas.length > 0 || empresa.carpetaAudiosId) && (
            <div className="px-4 py-2 text-xs space-y-1 border-t border-gray-100">
              {empresa.carpetaAudiosId && (
                <a
                  href={`https://drive.google.com/drive/folders/${empresa.carpetaAudiosId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline inline-flex items-center gap-1"
                >
                  <FolderOpen className="w-3.5 h-3.5" /> Abrir carpeta Audios
                </a>
              )}
              {empresa.problemas.map((p) => (
                <p key={p.codigo} className={p.severidad === 'error' ? 'text-red-700' : 'text-amber-700'}>
                  <AlertTriangle className="w-3 h-3 inline mr-1" />
                  {p.mensaje}
                </p>
              ))}
            </div>
          )}

          {empresa.asesorias.map((as) => (
            <AsesoriaTabla key={as.asesoriaId} asesoria={as} soloConProblemas={soloConProblemas} />
          ))}

          {empresa.huerfanos.length > 0 && (
            <div className="border-t border-gray-100 px-4 py-2">
              <p className="text-xs font-semibold text-amber-800 mb-1 flex items-center gap-1">
                <FileWarning className="w-3.5 h-3.5" /> Archivos en la carpeta que no corresponden a ningún audio registrado
              </p>
              <ul className="text-xs space-y-0.5">
                {empresa.huerfanos.map((h) => (
                  <li key={h.fileId} className="font-mono">
                    {h.url ? (
                      <a href={h.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        {h.nombre}
                      </a>
                    ) : (
                      h.nombre
                    )}
                    {!h.formatoValido && <span className="ml-2 text-[10px] text-red-600 font-sans">(formato inválido)</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

export function AuditoriaNombresAudiosPanel() {
  const [data, setData] = useState<AuditoriaNombresAudiosResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [soloConProblemas, setSoloConProblemas] = useState(true);

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      setData(await sysadminService.auditarNombresAudios());
    } catch (err: any) {
      setError(err.message || 'Error al auditar nombres de audios');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const empresas = useMemo(() => {
    if (!data) return [];
    const q = searchTerm.trim().toLowerCase();
    return data.empresas.filter(
      (e) => (!soloConProblemas || empresaTieneProblemas(e)) && (q === '' || e.nombre.toLowerCase().includes(q) || e.nit.includes(q))
    );
  }, [data, searchTerm, soloConProblemas]);

  if (isLoading) {
    return <LoadingContainer text="Revisando la carpeta de audios de cada empresa en Drive (puede tardar)..." />;
  }

  if (error || !data) {
    return (
      <Card className="p-8 text-center text-red-600 bg-red-50 border-red-200">
        <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-red-500" />
        <p className="font-semibold text-sm mb-4">{error || 'No se pudieron cargar los datos'}</p>
        <Button onClick={fetchData} variant="outline" size="sm">
          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Reintentar
        </Button>
      </Card>
    );
  }

  const metricas = [
    { label: 'Empresas revisadas', valor: data.resumen.empresas, icon: Building2, color: 'blue' },
    { label: 'Empresas con problemas', valor: data.resumen.empresasConProblemas, icon: AlertTriangle, color: 'rose' },
    { label: 'Audios revisados', valor: data.resumen.audios, icon: Music, color: 'purple' },
    { label: 'Audios con problemas', valor: data.resumen.audiosConProblemas, icon: FileWarning, color: 'amber' },
  ] as const;
  const colores = {
    blue: 'bg-blue-50/50 border-blue-200 text-blue-700',
    rose: 'bg-rose-50/50 border-rose-200 text-rose-700',
    purple: 'bg-purple-50/50 border-purple-200 text-purple-700',
    amber: 'bg-amber-50/50 border-amber-200 text-amber-700',
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-5 rounded-xl border border-gray-200 shadow-xs">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-base font-bold text-gray-900">Auditoría de Nombres de Audios</h2>
            <Badge variant="warning" className="text-[11px] font-semibold">
              Solo Sysadmin
            </Badge>
          </div>
          <p className="text-xs text-gray-500 mt-0.5">
            Formato esperado: <span className="font-mono text-gray-700">{data.formato}</span>. Se cruza la carpeta "Audios" de cada empresa
            en Drive con los audios registrados. Solo lectura.
          </p>
        </div>
        <Button onClick={fetchData} variant="outline" size="sm" className="text-xs text-gray-700 hover:bg-gray-50">
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
          Actualizar
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {metricas.map(({ label, valor, icon: Icon, color }) => (
          <Card key={label} className={`p-4 ${colores[color]}`}>
            <div className="flex items-center justify-between">
              <span className="text-[11px] uppercase tracking-wider font-bold">{label}</span>
              <Icon className="w-4 h-4" />
            </div>
            <p className="text-2xl font-black text-gray-900 mt-1">{valor}</p>
          </Card>
        ))}
      </div>

      <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-xs flex flex-col md:flex-row md:items-center justify-between gap-3">
        <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 cursor-pointer">
          <input type="checkbox" checked={soloConProblemas} onChange={(e) => setSoloConProblemas(e.target.checked)} />
          Mostrar solo lo que tiene problemas
        </label>
        <div className="relative">
          <Search className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar por empresa o NIT..."
            className="pl-8 pr-3 py-1.5 border border-gray-200 rounded-lg text-xs w-64 focus:outline-2 focus:outline-gray-900"
          />
        </div>
      </div>

      <div className="space-y-3">
        {empresas.length === 0 ? (
          <Card className="p-8 text-center text-gray-500 text-xs">No hay empresas con los filtros aplicados.</Card>
        ) : (
          empresas.map((e) => <EmpresaCard key={e.nit} empresa={e} soloConProblemas={soloConProblemas} />)
        )}
      </div>
    </div>
  );
}
